import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { CustomerSearchSelect } from './CustomerSearchSelect';

/**
 * Pick who the work is for, then where. Property depends on customer, so they live in
 * one component: choosing a customer reloads the addresses and clears a stale pick,
 * which is the bug you get every time these are wired up as two independent selects.
 *
 * A customer with exactly one address selects it automatically — that's most residential
 * work, and making someone confirm the only possible answer is not a choice.
 */
export function CustomerPicker({
  customerId,
  propertyId,
  onChange,
  requireProperty = true,
}: {
  customerId: string;
  propertyId: string;
  onChange: (next: { customerId: string; propertyId: string }) => void;
  requireProperty?: boolean;
}) {
  const trpc = useTRPC();
  const detail = useQuery({
    ...trpc.customer.get.queryOptions({ customerId }),
    enabled: Boolean(customerId),
  });

  const properties = detail.data?.properties ?? [];

  const pickCustomer = (nextCustomerId: string) => {
    // Never carry the old customer's address forward.
    onChange({ customerId: nextCustomerId, propertyId: '' });
  };

  // Auto-select the only address once it arrives. In an effect, not during render —
  // calling the parent's setState mid-render is the classic "cannot update a component
  // while rendering a different component" warning.
  const onlyPropertyId = properties.length === 1 ? properties[0]!.id : null;
  useEffect(() => {
    if (customerId && onlyPropertyId && propertyId !== onlyPropertyId) {
      onChange({ customerId, propertyId: onlyPropertyId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onChange identity is unstable by design
  }, [customerId, onlyPropertyId, propertyId]);

  const field =
    'h-10 w-full rounded-[var(--radius)] border border-border bg-surface px-2 text-sm';

  return (
    <div className="space-y-2">
      {/* Was a <select> holding every customer in the shop — see CustomerSearchSelect for
          why that was worse than slow. */}
      <CustomerSearchSelect value={customerId} onChange={pickCustomer} />

      {customerId ? (
        <label className="block text-xs font-medium">
          <span className="mb-1 block text-muted-foreground">Service address</span>
          {detail.isPending ? (
            <p className="py-2 text-sm text-muted-foreground">Loading addresses…</p>
          ) : properties.length === 0 ? (
            <p className="py-2 text-sm text-[var(--sw-warn-text)]">
              This customer has no service address yet — add one on their page first.
            </p>
          ) : (
            <select
              className={field}
              value={propertyId}
              onChange={(e) => onChange({ customerId, propertyId: e.target.value })}
            >
              {!requireProperty ? <option value="">No specific address</option> : null}
              {properties.length !== 1 ? (
                <option value="">Choose an address…</option>
              ) : null}
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                  {p.address1 ? ` — ${p.address1}` : ''}
                </option>
              ))}
            </select>
          )}
        </label>
      ) : null}
    </div>
  );
}
